import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Card, Alert, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { toggleOption, isOptionSelected as checkOptionSelected } from '../../shared/activityUtils';
import { DEFAULT_CURRENCY } from '../../shared/constants';
import { getSupportedCurrenciesSync } from '../../shared/currencyHelpers';
import { useModalBackHandler } from '../../shared/modalBackHandler';
import { formatForDateTimeLocal } from '../../shared/utils';
import { isRTL } from '../../i18n/config';
import { getCachedAccountData } from '../../shared/accountCache';
import LinkIconDropdown from '../LinkIconDropdown';

export default function TransportationModal({ 
  show, 
  onHide, 
  activity, 
  onSave, 
  dayId, 
  isNew = false,
  parsedData,
  planLanguage = 'en',
  onSwitchToAI,
  accountSettings
}) {
  const { t, i18n } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Check if plan was created in non-English language
  const isNonEnglishPlan = planLanguage && planLanguage !== 'en';
  const [currencies, setCurrencies] = useState(getSupportedCurrenciesSync());
  const [showGenerateActivityTip, setShowGenerateActivityTip] = useState(false);
  const [accountImages, setAccountImages] = useState([]);
  
  // Initialize with transportation-specific fields
  const [formData, setFormData] = useState({
    id: '',
    type: 'transportation',
    name: '',
    nameInEnglish: '',
    description: '',
    notes: '',
    startDateTime: '',
    endDateTime: '',
    cost: { value: '', currency: DEFAULT_CURRENCY },
    fromLocation: { address: '', addressInEnglish: '', latitude: '', longitude: '' },
    toLocation: { address: '', addressInEnglish: '', latitude: '', longitude: '' },
    links: [],
    images: [],
    completed: false,
    option: [] // Can be array [1,2,3], "all", or [] (empty array means all options)
  });
  
  // Load currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      const { getAllCurrencies } = await import('../../shared/currencyService');
      const loadedCurrencies = await getAllCurrencies();
      if (loadedCurrencies && loadedCurrencies.length > 0) {
        setCurrencies(loadedCurrencies.map(c => ({
          code: c.code,
          name: c.name || c.code
        })));
      }
    };
    loadCurrencies();
  }, []);

  // Check if we should show the generate activity tip (only once, only for new activities)
  useEffect(() => {
    if (!show || !isNew || activity) return; // Only show for new activities, not when editing
    
    const hasSeenTip = localStorage.getItem('tripsy_has_seen_generate_activity_tip');
    if (!hasSeenTip) {
      setShowGenerateActivityTip(true);
    }
  }, [show, isNew, activity]);

  // Load account images when modal opens so brand icons are available in link dropdown
  useEffect(() => {
    if (show) {
      getCachedAccountData().then((acc) => {
        setAccountImages(acc?.settings?.images || []);
      });
    }
  }, [show]);

  // Load activity data when modal is opened
  useEffect(() => {
    // Only reset when modal is shown
    if (!show) return;
    
    if (activity) {
      // Edit existing activity
      setFormData({
        id: activity.id || '',
        type: 'transportation',
        name: activity.name || '',
        nameInEnglish: activity.nameInEnglish || '',
        description: activity.description || '',
        notes: activity.notes || '',
        startDateTime: activity.startDateTime || '',
        endDateTime: activity.endDateTime || '',
        cost: activity.cost || { value: '', currency: parsedData?.currency || DEFAULT_CURRENCY },
        fromLocation: activity.fromLocation || { address: '', addressInEnglish: '', latitude: '', longitude: '' },
        toLocation: activity.toLocation || { address: '', addressInEnglish: '', latitude: '', longitude: '' },
        links: activity.links || [],
        images: activity.images || [],
        completed: activity.completed || false,
        option: activity.option !== undefined ? activity.option : [] // Can be array [1,2,3], "all", or [] (empty array defaults to option 1)
      });
    } else if (isNew) {
      // Create new transportation with default values
      
      // Set default date based on selected day
      let defaultStartTime = '';
      let defaultEndTime = '';
      
      // Find the day data to get its date
      const dayData = parsedData?.days?.find(day => day.id === dayId);
      if (dayData && dayData.date) {
        // Set default time to noon (12:00) on the selected day
        const dayPrefix = dayData.date.split('T')[0]; // Get YYYY-MM-DD part
        defaultStartTime = `${dayPrefix}T12:00:00.000Z`;
        // Set default end time to 1 hour later (13:00) for transportation
        defaultEndTime = `${dayPrefix}T13:00:00.000Z`;
      }
      
      // Reset form with defaults (don't use spread operator to avoid keeping old data)
      setFormData({
        id: '',
        type: 'transportation',
        name: '',
        nameInEnglish: '',
        description: '',
        notes: '',
        startDateTime: defaultStartTime,
        endDateTime: defaultEndTime,
        cost: { value: '', currency: parsedData?.currency || DEFAULT_CURRENCY },
        fromLocation: { address: '', addressInEnglish: '', latitude: '', longitude: '' },
        toLocation: { address: '', addressInEnglish: '', latitude: '', longitude: '' },
        links: [],
        images: [],
        option: [], // Default to option 1 (empty array means option 1)
        completed: false
      });
    }
  }, [show, activity, isNew, dayId, parsedData]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle cost as a special case with nested object
    if (name === 'costValue') {
      const cleanValue = value.replace(/,/g, '');
      setFormData({
        ...formData,
        cost: {
          ...formData.cost,
          value: cleanValue
        }
      });
    } else if (name === 'costCurrency') {
      setFormData({
        ...formData,
        cost: {
          ...formData.cost,
          currency: value
        }
      });
    } else if (name.startsWith('fromLocation.')) {
      // Handle nested fromLocation properties
      const locationProperty = name.split('.')[1];
      setFormData({
        ...formData,
        fromLocation: {
          ...formData.fromLocation,
          [locationProperty]: value
        }
      });
    } else if (name.startsWith('toLocation.')) {
      // Handle nested toLocation properties
      const locationProperty = name.split('.')[1];
      setFormData({
        ...formData,
        toLocation: {
          ...formData.toLocation,
          [locationProperty]: value
        }
      });
    } else if (name === 'nameInEnglish') {
      setFormData({
        ...formData,
        nameInEnglish: value
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Handle adding a new link
  const handleAddLink = () => {
    setFormData({
      ...formData,
      links: [...formData.links, { name: '', url: '', isPrivate: true, icon: '' }]
    });
  };
  
  // Handle removing a link
  const handleRemoveLink = (index) => {
    const updatedLinks = [...formData.links];
    updatedLinks.splice(index, 1);
    setFormData({
      ...formData,
      links: updatedLinks
    });
  };
  
  // Handle link field changes
  const handleLinkChange = (index, field, value) => {
    const updatedLinks = [...formData.links];
    updatedLinks[index] = {
      ...updatedLinks[index],
      [field]: value
    };
    setFormData({
      ...formData,
      links: updatedLinks
    });
  };

  // Handle adding a new image
  const handleAddImage = () => {
    if (formData.images.length < 2) {
      setFormData({
        ...formData,
        images: [...formData.images, { type: '', url: '' }]
      });
    }
  };
  
  // Handle removing an image
  const handleRemoveImage = (index) => {
    const updatedImages = [...formData.images];
    updatedImages.splice(index, 1);
    setFormData({
      ...formData,
      images: updatedImages
    });
  };
  
  // Handle image field changes
  const handleImageChange = (index, field, value) => {
    const updatedImages = [...formData.images];
    updatedImages[index] = {
      ...updatedImages[index],
      [field]: value
    };
    setFormData({
      ...formData,
      images: updatedImages
    });
  };

  // Handle option changes (multi-select)
  const handleOptionChange = (optionValue) => {
    setFormData(prev => ({
      ...prev,
      option: toggleOption(prev.option, optionValue)
    }));
  };

  // Check if an option is selected
  const isOptionSelected = (optionValue) => {
    return checkOptionSelected(formData.option, optionValue);
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create a proper transportation activity with the form data
    onSave(formData, dayId);
    onHide();
  };
  
  // Use our custom hook to handle browser back button
  useModalBackHandler(show, onHide);
  
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isNew ? t('planDetail.activityModals.addTransportation') : t('planDetail.activityModals.editTransportation')}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {showGenerateActivityTip && (
          <Alert variant="info" dismissible onClose={() => {
            setShowGenerateActivityTip(false);
            localStorage.setItem('tripsy_has_seen_generate_activity_tip', 'true');
          }} className="mb-3">
            <Alert.Heading>
              <i className="bi bi-lightbulb me-2"></i>
              {t('planDetail.activityModals.tipTitle')}
            </Alert.Heading>
            <p className="mb-0">
              {t('planDetail.activityModals.tipDescription')}
            </p>
            {onSwitchToAI && (
              <div className="mt-2">
                <Button variant="primary" size="sm" onClick={() => {
                  setShowGenerateActivityTip(false);
                  localStorage.setItem('tripsy_has_seen_generate_activity_tip', 'true');
                  onSwitchToAI();
                }}>
                  <i className="bi bi-magic me-1"></i>
                  {t('planDetail.activityModals.tryAddActivityWithAI')}
                </Button>
              </div>
            )}
          </Alert>
        )}
        <Form id="transportation-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              {t('planDetail.activityModals.name')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control 
              type="text" 
              name="name" 
              value={formData.name} 
              onChange={handleInputChange}
              placeholder={t('planDetail.activityModals.placeholder.transportationName')}
              required
            />
          </Form.Group>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('planDetail.activityModals.startDateTime')}</Form.Label>
                <Form.Control 
                  type="datetime-local" 
                  name="startDateTime" 
                  value={formatForDateTimeLocal(formData.startDateTime)} 
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('planDetail.activityModals.endDateTime')}</Form.Label>
                <Form.Control 
                  type="datetime-local" 
                  name="endDateTime" 
                  value={formatForDateTimeLocal(formData.endDateTime)} 
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">{t('planDetail.activityModals.description')}</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              name="description" 
              value={formData.description} 
              onChange={handleInputChange}
              placeholder={t('planDetail.activityModals.placeholder.transportationDescription')}
            />
          </Form.Group>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('planDetail.activityModals.cost')}</Form.Label>
                <Form.Control 
                  type="text" 
                  name="costValue" 
                  value={formData.cost.value} 
                  onChange={handleInputChange}
                  placeholder={t('planDetail.activityModals.placeholder.costAmount')}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('planDetail.activityModals.currency')}</Form.Label>
                <Form.Select 
                  name="costCurrency" 
                  value={formData.cost.currency} 
                  onChange={handleInputChange}
                >
                  {currencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex align-items-center">
                <strong className="me-2">{t('planDetail.activityModals.fromLocation')}</strong>
                <i 
                  className="bi bi-question-circle-fill text-primary" 
                  style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                  data-tooltip-id="from-location-tooltip"
                  data-tooltip-html={`<div><strong>${t('planDetail.activityModals.locationTooltip.title')}</strong><br/><br/>${t('planDetail.activityModals.locationTooltip.description')}</div>`}
                ></i>
                <Tooltip id="from-location-tooltip" place="right" className="activity-modal-tooltip" />
              </div>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.activityModals.address')}</Form.Label>
                <Form.Control 
                  type="text" 
                  name="fromLocation.address" 
                  value={formData.fromLocation?.address || ''} 
                  onChange={handleInputChange}
                  placeholder={t('planDetail.activityModals.placeholder.departureLocation')}
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('planDetail.activityModals.latitude')}</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="fromLocation.latitude" 
                      value={formData.fromLocation?.latitude || ''} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.latitude')}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-0">
                    <Form.Label>{t('planDetail.activityModals.longitude')}</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="fromLocation.longitude" 
                      value={formData.fromLocation?.longitude || ''} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.longitude')}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex align-items-center">
                <strong className="me-2">{t('planDetail.activityModals.toLocation')}</strong>
                <i 
                  className="bi bi-question-circle-fill text-primary" 
                  style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                  data-tooltip-id="to-location-tooltip"
                  data-tooltip-html={`<div><strong>${t('planDetail.activityModals.locationTooltip.title')}</strong><br/><br/>${t('planDetail.activityModals.locationTooltip.description')}</div>`}
                ></i>
                <Tooltip id="to-location-tooltip" place="right" className="activity-modal-tooltip" />
              </div>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.activityModals.address')}</Form.Label>
                <Form.Control 
                  type="text" 
                  name="toLocation.address" 
                  value={formData.toLocation?.address || ''} 
                  onChange={handleInputChange}
                  placeholder={t('planDetail.activityModals.placeholder.arrivalLocation')}
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('planDetail.activityModals.latitude')}</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="toLocation.latitude" 
                      value={formData.toLocation?.latitude || ''} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.latitude')}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-0">
                    <Form.Label>{t('planDetail.activityModals.longitude')}</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="toLocation.longitude" 
                      value={formData.toLocation?.longitude || ''} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.longitude')}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          {/* Advanced Section */}
          <Card className="mb-3">
            <Card.Header
              onClick={() => setShowAdvanced(!showAdvanced)}
              style={{ cursor: 'pointer' }}
              className="d-flex justify-content-between align-items-center"
            >
              <strong>{t('planDetail.activityModals.advanced')}</strong>
              <i className={`bi bi-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
            </Card.Header>
            <Collapse in={showAdvanced}>
              <Card.Body>
                {/* Name in English - only if plan is not English */}
                {isNonEnglishPlan && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center">
                      <span className="me-2">{t('planDetail.activityModals.nameInEnglish')}</span>
                      <i 
                        className="bi bi-question-circle-fill text-primary" 
                        style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                        data-tooltip-id="transportation-name-english-tooltip"
                        data-tooltip-html={t('planDetail.activityModals.nameInEnglishTooltip')}
                      ></i>
                      <Tooltip id="transportation-name-english-tooltip" place="right" className="activity-modal-tooltip" />
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      name="nameInEnglish" 
                      value={formData.nameInEnglish} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.nameInEnglish')}
                      dir="ltr"
                    />
                  </Form.Group>
                )}
                
                {/* From Address in English - only if plan is not English */}
                {isNonEnglishPlan && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center">
                      <span className="me-2">{t('planDetail.activityModals.fromLocation')} - {t('planDetail.activityModals.addressInEnglish')}</span>
                      <i 
                        className="bi bi-question-circle-fill text-primary" 
                        style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                        data-tooltip-id="transportation-from-address-english-tooltip"
                        data-tooltip-html={t('planDetail.activityModals.addressInEnglishTooltip')}
                      ></i>
                      <Tooltip id="transportation-from-address-english-tooltip" place="right" className="activity-modal-tooltip" />
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      name="fromLocation.addressInEnglish" 
                      value={formData.fromLocation.addressInEnglish} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.addressInEnglish')}
                      dir="ltr"
                    />
                  </Form.Group>
                )}
                
                {/* To Address in English - only if plan is not English */}
                {isNonEnglishPlan && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center">
                      <span className="me-2">{t('planDetail.activityModals.toLocation')} - {t('planDetail.activityModals.addressInEnglish')}</span>
                      <i 
                        className="bi bi-question-circle-fill text-primary" 
                        style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                        data-tooltip-id="transportation-to-address-english-tooltip"
                        data-tooltip-html={t('planDetail.activityModals.addressInEnglishTooltip')}
                      ></i>
                      <Tooltip id="transportation-to-address-english-tooltip" place="right" className="activity-modal-tooltip" />
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      name="toLocation.addressInEnglish" 
                      value={formData.toLocation.addressInEnglish} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.addressInEnglish')}
                      dir="ltr"
                    />
                  </Form.Group>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{t('planDetail.activityModals.notes')}</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={2} 
                    name="notes" 
                    value={formData.notes} 
                    onChange={handleInputChange}
                    placeholder={t('planDetail.activityModals.placeholder.transportationNotes')}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <span className="me-2">{t('planDetail.activityModals.itineraryOptions')}</span>
                    <i 
                      className="bi bi-question-circle-fill text-primary" 
                      style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                      data-tooltip-id="transportation-options-tooltip"
                      data-tooltip-html={t('planDetail.activityModals.optionTooltip')}
                    ></i>
                    <Tooltip id="transportation-options-tooltip" place="right" className="activity-modal-tooltip" />
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    <Form.Check
                      type="checkbox"
                      id="transport-option-1"
                      label={`${t('planDetail.days.option')} 1`}
                      checked={isOptionSelected('1')}
                      onChange={() => handleOptionChange('1')}
                    />
                    <Form.Check
                      type="checkbox"
                      id="transport-option-2"
                      label={`${t('planDetail.days.option')} 2`}
                      checked={isOptionSelected('2')}
                      onChange={() => handleOptionChange('2')}
                    />
                    <Form.Check
                      type="checkbox"
                      id="transport-option-3"
                      label={`${t('planDetail.days.option')} 3`}
                      checked={isOptionSelected('3')}
                      onChange={() => handleOptionChange('3')}
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <span className="me-2">{t('planDetail.activityModals.links')}</span>
                    <i 
                      className="bi bi-question-circle-fill text-primary" 
                      style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                      data-tooltip-id="transportation-links-tooltip"
                      data-tooltip-html={`<div style="font-weight: normal;"><strong>${t('planDetail.activityModals.linksTooltip.title')}</strong><br/><br/>${t('planDetail.activityModals.linksTooltip.description')}</div>`}
                    ></i>
                    <Tooltip id="transportation-links-tooltip" place="right" className="activity-modal-tooltip" />
                  </Form.Label>
                  {formData.links.map((link, index) => (
                    <Row key={index} className={`mb-3 align-items-center gap-2 ${index % 2 === 0 ? 'bg-light' : ''}`} style={{ padding: '0.5rem', borderRadius: '0.25rem' }}>
                      <Col xs={12} md={3}>
                        <Form.Control
                          type="text"
                          placeholder={t('planDetail.activityModals.linkName')}
                          value={link.name || ''}
                          onChange={(e) => handleLinkChange(index, 'name', e.target.value)}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Control
                          type="url"
                          placeholder={t('planDetail.activityModals.url')}
                          value={link.url || ''}
                          onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                          dir="ltr"
                        />
                      </Col>
                      <Col xs={12} md={2}>
                        <LinkIconDropdown
                          value={link.icon ?? ''}
                          onChange={(val) => handleLinkChange(index, 'icon', val)}
                          accountImages={[...(accountSettings?.images || []), ...(accountImages || [])].filter((img, i, arr) => img?.type === 'icon' && img?.url && arr.findIndex((x) => x?.url === img?.url) === i)}
                          t={t}
                          size="sm"
                          ariaLabel={t('planDetail.activityModals.linkIcon', { defaultValue: 'Icon' })}
                        />
                      </Col>
                      <Col xs={12} md={3} className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="checkbox"
                          label={t('planDetail.activityModals.private')}
                          checked={link.isPrivate !== false}
                          onChange={(e) => handleLinkChange(index, 'isPrivate', e.target.checked)}
                          className="small mb-0 private-link-checkbox"
                        />
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleRemoveLink(index)}
                          className={isRTL(i18n.language) ? 'me-auto' : 'ms-auto'}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={handleAddLink}
                    className="mt-2"
                  >
                    <i className="bi bi-plus-circle me-1"></i> {t('planDetail.activityModals.addLink')}
                  </Button>
                </Form.Group>
                
                {/* Images management interface */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{t('planDetail.activityModals.images')}</Form.Label>
                  {formData.images.map((image, index) => (
                    <div key={index} className={`mb-3 ${index % 2 === 0 ? 'bg-light' : ''}`} style={{ padding: '0.5rem', borderRadius: '0.25rem' }}>
                      <Row className="mb-2 gap-2 align-items-center">
                        <Col xs={12} md={5}>
                          <Form.Control
                            type="text"
                            placeholder={t('planDetail.activityModals.title')}
                            value={image.type || ''}
                            onChange={(e) => handleImageChange(index, 'type', e.target.value)}
                          />
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Control
                            type="url"
                            placeholder={t('planDetail.activityModals.imageUrl')}
                            value={image.url || ''}
                            onChange={(e) => handleImageChange(index, 'url', e.target.value)}
                            dir="ltr"
                          />
                        </Col>
                        <Col xs={12} md={1} className="d-flex align-items-center">
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleRemoveImage(index)}
                            className={isRTL(i18n.language) ? 'me-auto' : 'ms-auto'}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </Col>
                      </Row>
                      {image.url && (
                        <div className="mt-2">
                          <img 
                            src={image.url} 
                            alt={image.type || 'Preview'} 
                            className="img-thumbnail"
                            style={{ 
                              maxWidth: '150px', 
                              maxHeight: '100px',
                              objectFit: 'contain',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={handleAddImage}
                    className="mt-2"
                  >
                    <i className="bi bi-plus-circle me-1"></i> {t('planDetail.activityModals.addImage')}
                  </Button>
                </Form.Group>
              </Card.Body>
            </Collapse>
          </Card>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" form="transportation-form">
          {isNew ? t('planDetail.activityModals.addTransportation') : t('planDetail.activityModals.saveChanges')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
} 